import { useEffect, useRef, useState } from "react";

import { faMessageLines } from "@fortawesome/pro-regular-svg-icons";
import { twJoin } from "tailwind-merge";

import { mdService } from "src/base-components/TextEditor/MarkdownService";
import { HistorySingleEntry } from "src/cases/caseView/history/HistoryItemFrame";
import { CaseHistoryEvent } from "src/cases/queries/casesTypes";

export const CommentAdded: React.FC<{
  entry: CaseHistoryEvent<"comment_added">;
  isLast: boolean;
}> = ({ entry, isLast }) => {
  const [expanded, setExpanded] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);
  const isContentClamped = useIsContentClamped(contentRef);

  return (
    <HistorySingleEntry
      actorId={entry.event_data.user_id}
      content={
        <div className="tktl-markdown rounded-lg bg-gray-50 p-2 font-inter-normal-12px text-gray-700">
          <div
            //skipcq: JS-0440
            dangerouslySetInnerHTML={{
              __html: mdService.toHtml(entry.event_data.comment_text),
            }}
            ref={contentRef}
            className={twJoin(!expanded ? "line-clamp-3" : "line-clamp-none")}
          />

          {isContentClamped && (
            <div className="flex justify-end">
              <button
                className="text-indigo-600"
                onClick={() => setExpanded(!expanded)}
              >
                {expanded ? "See less" : "See more"}
              </button>
            </div>
          )}
        </div>
      }
      eventType={entry.event_type}
      headline="A comment was added to the case."
      icon={faMessageLines}
      isLast={isLast}
      timestamp={entry.timestamp}
    />
  );
};

/**
 * If content at least once overflows the container,
 * this hook starts returning true.
 */
const useIsContentClamped = <T extends HTMLElement>(
  ref: React.RefObject<T>,
) => {
  const [isContentClamped, setIsContentClamped] = useState(false);

  useEffect(() => {
    const updateClampedState = () => {
      const el = ref.current;

      if (el && !isContentClamped && el.scrollHeight > el.clientHeight) {
        setIsContentClamped(true);
      }
    };

    updateClampedState();
    window.addEventListener("resize", updateClampedState);

    return () => {
      window.removeEventListener("resize", updateClampedState);
    };
  }, [ref, isContentClamped]);

  return isContentClamped;
};
